/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

import React from 'react';
import { observer } from 'mobx-react';
import { Card } from '@react/react-spectrum/Card';
import classnames from 'classnames';
import { interpretKey } from '../../../../utils/eventUtil';
import CardGallery from '../../../../components/appCardGallery';
import { appTileAnalytics } from '../../../../analytics';
import { ANALYTICS } from '../../../../utils/enums';
import AppTileHeader from './header';
import AppTileFooter from '../../../../components/footer';
import { getContentExt } from '../../../../utils/commonUtil';
import './index.scss';

class MediaCard extends React.Component {
    onTileClick = (app, appSectionFilter, mediaList, isCategorySection) => {
        const {
            onAppAction,
            actionIds,
        } = app;
        const { layoutId, cardSize } = this.props;
        const contentSize = cardSize === '33-percent' ? '33' : '50';
        onAppAction(actionIds.select, app, { isCategorySection });
        appTileAnalytics(ANALYTICS.EVENTS.APP_TILE_CLICK, {
            app,
            appSectionFilter,
            actionId: actionIds.select,
            viewType: ANALYTICS.VIEW_TYPE[layoutId],
            contentSize,
            contentExt: getContentExt(mediaList),
        });
    }

    renderCardGallery = ({
        mediaList, app, appSectionFilter,
        isHeaderClickable, isNonDownloadableSurface, isCategorySection,
    }) => {
        const mediaClassName = classnames('', {
            clickable: (isHeaderClickable
            && (app.isPDPDataAvailable || app.isLaunchable)),
        });
        const onMediaClick = isHeaderClickable && (app.isPDPDataAvailable || app.isLaunchable)
            ? () => {
                this.onTileClick(app, appSectionFilter, mediaList, isCategorySection);
            } : undefined;
        return (
            <div
                className="mediaCardView-appTile-gallery"
                ref={(element) => { this.gallery = element; }}>
                <CardGallery
                    mediaList={mediaList}
                    app={app}
                    mediaClassName={mediaClassName}
                    onMediaClick={onMediaClick}
                    isMobileOrTab={isNonDownloadableSurface} />
            </div>
        );
    }

    playCurrentVideo = () => {
        if (!this.gallery) {
            return;
        }
        if (this.isVideoPlaying) {
            return;
        }
        const media = this.gallery.querySelectorAll('.mediaCardView-appTile-gallery video, .mediaCardView-appTile-gallery .appCard-media-gallery-image');
        // find index of active pager to find current slide
        const pagers = this.gallery.querySelectorAll('.mediaCardView-appTile-gallery .carousel-pager-dot');
        let activeSlideInd = 0;
        let ind;
        for (ind = 0; ind < pagers.length; ind += 1) {
            const { classList } = pagers[ind];
            if (classList.contains('active')) {
                activeSlideInd = ind;
                break;
            }
        }
        if (media
            && media[activeSlideInd]
            && media[activeSlideInd].nodeName === 'VIDEO'
            && media[activeSlideInd].paused) {
            const videoPlayPromise = media[activeSlideInd].play();
            if (videoPlayPromise) {
                videoPlayPromise
                    .then(() => {
                        if (!this.isFocussed && !this.isMouseOver) {
                            this.pauseVideos();
                        } else {
                            this.isVideoPlaying = true;
                        }
                    })
                    .catch(() => {
                        this.isVideoPlaying = false;
                    });
            } else {
                this.isVideoPlaying = true;
            }
        }
    }

    pauseVideos = () => {
        this.isVideoPlaying = false;
        if (!this.gallery) {
            return;
        }
        const videos = this.gallery.querySelectorAll('.mediaCardView-appTile-gallery video');
        // bug fix CCH-9832: convert nodeList to Array
        const videoArr = Array.prototype.slice.call(videos, 0);
        // pause all videos on mouse out
        videoArr.forEach((video) => {
            video.pause();
        });
    }

    handleCarouselNav = (elem) => {
        if (elem) {
            elem.click();
            this.pauseVideos();
            setTimeout(this.playCurrentVideo, 0);
        }
    }

    render() {
        const {
            app, appSectionFilter, className,
            sectionHeader, subSectionFilter,
            appSection, layoutId, cardSize, appConfig = {},
        } = this.props;
        const selectedField = app.appSectionBasedFields.find((
            { appSectionFilter: appSectionFilterArray }
        ) => appSectionFilterArray.includes(appSectionFilter));
        const { onAppAction, actionIds } = app;

        if (!selectedField) {
            return [];
        }

        const { isHeaderClickable, isCategorySection } = selectedField;

        const cardContextId = `${String(app.sapcode).toUpperCase()}_${app.codexVersion}`;
        // get header based values
        // TODO: check subSection for sectionAppsData also
        let headerBasedAppData = {};
        if (appSection && appSection.sectionAppsData && appSection.sectionAppsData[app.sapcode]) {
            headerBasedAppData = appSection.sectionAppsData[app.sapcode];
        }
        const { mediaList = [] } = headerBasedAppData;
        const { isNonDownloadableSurface } = appConfig;

        return (
            <Card
                className={className}
                allowsSelection={false}
                tabIndex={0}
                onMouseDown={(e) => { e.preventDefault(); }}
                onMouseOver={(e) => {
                    e.stopPropagation();
                    this.isMouseOver = true;
                    this.playCurrentVideo();
                }}
                onFocus={(e) => {
                    e.stopPropagation();
                    this.isFocussed = true;
                    this.playCurrentVideo();
                }}
                onMouseOut={(e) => {
                    e.stopPropagation();
                    this.isMouseOver = false;
                    if (this.isVideoPlaying && !this.isFocussed) {
                        this.pauseVideos();
                    }
                }}
                onMouseLeave={(e) => {
                    e.stopPropagation();
                    this.isMouseOver = false;
                    // Bug fix CCH-9699
                    if (this.isVideoPlaying && !this.isFocussed) {
                        this.pauseVideos();
                    }
                }}
                onBlur={(e) => {
                    e.stopPropagation();
                    this.isFocussed = false;
                    // Bug fix CCH-9699
                    if (this.isVideoPlaying) {
                        this.pauseVideos();
                    }
                }}
                onKeyDown={interpretKey({
                    onEnter: [() => onAppAction(actionIds.select, app),
                        true,
                    ],
                    onLeftArrow: [(e) => {
                        const leftCarouselBtn = e.target.querySelectorAll('.carousel-left-button');
                        if (leftCarouselBtn
                            && leftCarouselBtn[0]) {
                            this.handleCarouselNav(leftCarouselBtn[0]);
                        }
                    },
                    true,
                    ],
                    onRightArrow: [(e) => {
                        const rightCarouselBtn = e.target.querySelectorAll('.carousel-right-button');
                        if (rightCarouselBtn
                            && rightCarouselBtn[0]) {
                            this.handleCarouselNav(rightCarouselBtn[0]);
                        }
                    },
                    true,
                    ],
                })}
                role="link">
                {mediaList
                    && this.renderCardGallery({
                        mediaList,
                        app,
                        appSectionFilter,
                        isHeaderClickable,
                        isNonDownloadableSurface,
                        isCategorySection,
                    })}
                <div
                    className={`mediaCardView_appTile_container ${cardContextId}`}
                    render-guid={cardContextId}
                >
                    <AppTileHeader
                        app={app}
                        appSectionBasedFields={selectedField}
                        sectionHeader={sectionHeader}
                        subSectionFilter={subSectionFilter}
                        appSectionFilter={appSectionFilter}
                        layoutId={layoutId}
                        headerBasedAppData={headerBasedAppData}
                        cardSize={cardSize}
                        mediaList={mediaList} />

                    <AppTileFooter
                        app={app}
                        appSectionBasedFields={selectedField}
                        appSectionFilter={appSectionFilter}
                        sectionHeader={sectionHeader}
                        mediaList={mediaList}
                        layoutId={layoutId} />
                </div>
            </Card>
        );
    }
}

export default observer(MediaCard);
